<template>
  <div class="indicator-result box box-default">
    <div class="box-header with-border">
      <h4 class="box-title">{{ indicator.title }}</h4>
      <div v-if="showFinancialToggle" class="box-tools pull-right financial-view-toggle">
        <label class="sr-only">Visualização financeira</label>
        <select v-model="financialView" class="form-control input-sm">
          <option value="nominal">Nominal</option>
          <option value="deflated">Deflacionado (IPCA)</option>
          <option value="withoutKnownParkOutliers">Sem outliers conhecidos</option>
        </select>
      </div>
    </div>
    <div class="box-body">
      <div v-if="showLineChart && lineChartData && chartVisible" class="chart-wrapper">
        <ReportChartContainer
          :key="'line-' + chartRenderKey + '-' + financialView"
          :data="lineChartData"
          :chart-component="LineChart"
        />
      </div>

      <div v-if="snapshotChartData && chartVisible" class="chart-wrapper snapshot-chart">
        <ReportChartContainer
          :key="'snapshot-' + chartRenderKey"
          :data="snapshotChartData"
          :chart-component="snapshotChartComponent"
        />
      </div>

      <div v-if="distributionChartData && chartVisible" class="chart-wrapper distribution-chart">
        <ReportChartContainer
          :key="'distribution-' + chartRenderKey + '-' + financialView"
          :data="distributionChartData"
          :chart-component="distributionChartComponent"
        />
      </div>

      <div v-if="(indicator.type === 'table' || indicator.type === 'ranking') && tableData" class="table-wrapper">
        <TableReport :data="tableData" />
      </div>

      <div
        v-for="(nestedTable, index) in nestedTables"
        :key="'nested-' + index"
        class="table-wrapper nested-table"
      >
        <TableReport :data="nestedTable" />
      </div>

      <div v-if="indicator.type === 'series' && seriesDetailTable" class="table-wrapper table-scroll">
        <TableReport :data="seriesDetailTable" />
      </div>

      <div v-if="comparisonTable" class="table-wrapper table-scroll">
        <TableReport :data="comparisonTable" />
      </div>

      <div
        v-for="(comparisonNestedTable, index) in comparisonNestedTables"
        :key="'comparison-' + index"
        class="table-wrapper table-scroll nested-table"
      >
        <TableReport :data="comparisonNestedTable" />
      </div>

      <p v-if="concentrationText" class="concentration-summary text-muted">
        <i class="fa fa-pie-chart"></i> {{ concentrationText }}
      </p>

      <div v-if="indicator.type === 'kpi'" class="kpi-value">
        {{ kpiValue }}
      </div>

      <p v-if="adjustmentNote" class="text-muted adjustment-note">
        <i class="fa fa-info-circle"></i> {{ adjustmentNote }}
      </p>
    </div>
  </div>
</template>

<script>
  import LineChart from '@/components/elements/charts/LineChart'
  import BarChart from '@/components/elements/charts/BarChart'
  import DoughnutChart from '@/components/elements/charts/DoughnutChart'
  import HorizontalBarChart from '@/components/elements/charts/HorizontalBarChart'
  import ReportChartContainer from '@/components/elements/ReportChartContainer'
  import TableReport from '@/components/elements/TableReport'
  import RelatorioEstatisticoFormatter from '@/services/relatorio_estatistico_formatter'

  export default {
    name: 'IndicatorResult',
    components: { ReportChartContainer, TableReport, LineChart, BarChart, DoughnutChart, HorizontalBarChart },
    props: {
      indicator: {
        type: Object,
        required: true
      },
      chartVisible: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        LineChart: LineChart,
        BarChart: BarChart,
        DoughnutChart: DoughnutChart,
        HorizontalBarChart: HorizontalBarChart,
        financialView: 'nominal',
        chartRenderKey: 0
      }
    },
    watch: {
      chartVisible (isVisible) {
        if (isVisible) {
          this.bumpChartRenderKey()
        }
      }
    },
    computed: {
      showFinancialToggle () {
        return this.indicator.type === 'series' &&
          RelatorioEstatisticoFormatter.hasFinancialAdjustments(this.indicator)
      },
      showLineChart () {
        return RelatorioEstatisticoFormatter.shouldShowHistoricalSeriesChart(this.indicator)
      },
      lineChartData () {
        if (this.indicator.type !== 'series') {
          return null
        }
        return RelatorioEstatisticoFormatter.seriesToChart(this.indicator, this.financialView)
      },
      snapshotChartData () {
        return RelatorioEstatisticoFormatter.snapshotToChart(this.indicator)
      },
      snapshotChartComponent () {
        return this.resolveChartComponent(this.snapshotChartData)
      },
      distributionChartData () {
        if (this.indicator.type !== 'table') {
          return null
        }
        return RelatorioEstatisticoFormatter.tableToDistributionChart(this.indicator, this.financialView)
      },
      distributionChartComponent () {
        return this.resolveChartComponent(this.distributionChartData)
      },
      tableData () {
        if (this.indicator.type === 'table') {
          if (this.showFinancialToggle) {
            return RelatorioEstatisticoFormatter.tableToReport(this.indicator, this.financialView)
          }
          return RelatorioEstatisticoFormatter.tableToReport(this.indicator)
        }
        if (this.indicator.type === 'ranking') {
          return RelatorioEstatisticoFormatter.rankingToReport(this.indicator)
        }
        return null
      },
      nestedTables () {
        if (this.indicator.type !== 'table') {
          return []
        }
        return RelatorioEstatisticoFormatter.nestedTablesToReports(this.indicator)
      },
      seriesDetailTable () {
        if (this.indicator.type !== 'series') {
          return null
        }
        return RelatorioEstatisticoFormatter.seriesDetailToReport(this.indicator)
      },
      comparisonTable () {
        return RelatorioEstatisticoFormatter.comparisonToReport(this.indicator)
      },
      comparisonNestedTables () {
        if (this.indicator.type !== 'table' && this.indicator.type !== 'ranking') {
          return []
        }
        return RelatorioEstatisticoFormatter.comparisonNestedTablesToReports(this.indicator)
      },
      concentrationText () {
        if (this.indicator.type !== 'ranking') {
          return null
        }
        return RelatorioEstatisticoFormatter.concentrationSummary(this.indicator)
      },
      kpiValue () {
        if (this.indicator.type !== 'kpi') {
          return null
        }
        return RelatorioEstatisticoFormatter.formatKpiValue(this.indicator)
      },
      adjustmentNote () {
        const adjustments = this.indicator.data && this.indicator.data.adjustments
        if (!adjustments) {
          return null
        }
        if (this.financialView === 'deflated') {
          return adjustments.note || null
        }
        if (this.financialView === 'withoutKnownParkOutliers' && adjustments.knownParkOutlierExclusions) {
          const resolved = adjustments.knownParkOutlierExclusions.filter(function (item) {
            return item.resolved
          })
          if (resolved.length === 0) {
            return 'Nenhum outlier conhecido foi aplicado (parques não encontrados no cadastro).'
          }
          return 'Exclusão de contribuições de parques com valores atípicos documentados na planilha manual.'
        }
        return null
      }
    },
    methods: {
      resolveChartComponent (chartData) {
        const layoutType = chartData && chartData.layout && chartData.layout.type
        if (layoutType === 'doughnut') {
          return DoughnutChart
        }
        if (layoutType === 'horizontalBar') {
          return HorizontalBarChart
        }
        if (layoutType === 'bar') {
          return BarChart
        }
        return BarChart
      },
      bumpChartRenderKey () {
        this.chartRenderKey += 1
        // Only when a section becomes visible: Chart.js needs a layout pass.
        // Do not fire this on financialView changes — a global resize rebuilds
        // every chart on the page and freezes the browser.
        this.$nextTick(function () {
          window.dispatchEvent(new Event('resize'))
        })
      }
    }
  }
</script>

<style scoped>
  .indicator-result {
    margin-bottom: 20px;
  }
  .chart-wrapper {
    text-align: center;
    margin-bottom: 16px;
    overflow: visible;
  }
  .snapshot-chart {
    padding-bottom: 12px;
  }
  .nested-table {
    margin-top: 16px;
  }
  .table-scroll {
    overflow-x: auto;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }
  .table-scroll >>> table {
    margin-bottom: 0;
    white-space: nowrap;
  }
  .financial-view-toggle select {
    width: 220px;
  }
  .kpi-value {
    font-size: 28px;
    font-weight: bold;
    color: #3c8dbc;
    text-align: center;
    padding: 16px 0;
  }
  .concentration-summary {
    margin-top: 12px;
    font-size: 13px;
  }
  .adjustment-note {
    margin-top: 12px;
    font-size: 12px;
  }
</style>



// WEBPACK FOOTER //
// IndicatorResult.vue?5cae0204